import React, { forwardRef, useEffect, useImperativeHandle} from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCIcon, RCCInput, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import SortableContainer from "src/components/Sortable/Container";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import { IStandardItemModel } from "src/components/StandardItems/interface";
import Title from "src/components/Title";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IRefTabAuditItems, ITabAuditItems } from "./interface";
import { IAuditItems } from "../../interface";

const TabAuditItems: React.ForwardRefRenderFunction<IRefTabAuditItems, ITabAuditItems> = ({
	auditItems,
	disabled,
	control,
	register,
	unregister,
	error,
	clearErrors,
	openModalStandardItems
}, ref) => {

	useImperativeHandle(ref, () => ({
		addItemStandardItem
	}));

	const { translate } = useTranslation();

	const { 
		fields: fieldsItem, 
		remove: removeItem,
		append: appendItem,
		move: movePhase
	} = useFieldArray({
		control,
		name: "adtbb004",
		shouldUnregister: true
	});

	const addNewItem = () => {
		clearErrors("adtbb004");
        
		const newItem = {
			adb004Id: 0,
			adb004adb001Id: 0,
			adb004Codigo: "",
			adb004Descricao: "",
			adb004Ordem: 0
		};

		appendItem(newItem);
	};

	const addItemStandardItem = (itens:Array<IStandardItemModel>) => {
		clearErrors("adtbb004");
    
		itens.forEach((item: IStandardItemModel) => {
			const newItem = {
				adb004Id: 0,
				adb004adb001Id: 0,
				adb004Codigo: item.ada002Codigo,
				adb004Descricao: item.ada002Descricao,
				adb004Ordem: 0
			};
	
			appendItem(newItem);
		});
	};

	const handleRemoveItem = (index: number) => {	
		clearErrors("adtbb004");		
		removeItem(index);
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = fieldsItem.findIndex((checks) => checks.id === activeId);
		const overIndex = fieldsItem.findIndex((checks) => checks.id === overId);
		movePhase(activeIndex, overIndex);
		unregister("adtbb004");
	};

	const onBlurItens = () => {
		if(error.adtbb004){
			clearErrors("adtbb004");
		}
	};

	useEffect(() => {
		appendItem(auditItems.map((items: IAuditItems) => ({
			...items,
			id: v4()
		})));
	}, []);

	return (
		<RCCGrid
			container
			item
			direction="column"
			alignItems="center"
		>
			<RCCGrid
				container
				item
				direction="column"
			>            
				<RCCGrid 
					container
					item
				>
					<RCCGrid
						container
						item
						xs={6}
						alignContent="center"
					>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar Item")}
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								onClick={() => addNewItem()}
								disabled={disabled}
							>
								{translate("Adicionar Item")}
							</RCCButton>
						</RCCTooltip>
						<RCCTooltip
							color="grey"
							text={translate("Selecionar Itens da Norma")}
						>
							<RCCButton
								color="grey"
								icon={{
									name: "plus"
								}}
								onClick={() => openModalStandardItems()}
								disabled={disabled}
							>
								{translate("Selecionar Itens da Norma")}
							</RCCButton>
						</RCCTooltip>
					</RCCGrid>
					
					<RCCGrid
						container
						item
						xs={6}
						alignContent="center"
						alignItems="center"
						justifyContent="flex-end"
					>
						{error.adtbb004 && (
							<>
								<RCCIcon name="infoCircle" size={18} color="var(--red)"/>
								<Title color="red" size="xs">{error.adtbb004.message}</Title>
							</>
						)}
					</RCCGrid>
				</RCCGrid>
				{
					fieldsItem.length === 0 &&
					<RCCAlert 
						title={translate("Atenção")}
						description={translate("Nenhum item encontrado")}
					/>
				}
				
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
					justifyContent="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						md={10}
					>
						<SortableContainer 
							items={fieldsItem}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToVerticalAxis]}
					    >
							{fieldsItem.map((itemAudit: any, index) => (
								<SortableItem id={itemAudit.id} key={v4()}>
									<RCCGrid
										container
										item
										alignItems="flex-end"
										justifyContent="space-between"
									>
										<input 
											name={`adtb004[${index}].adb004Id`} 
											type="text"
											value={itemAudit.adb004Id || 0}
											{...register(`adtbb004[${index}].adb004Id`)} 
											style={{ display: "none" }} 
											readOnly
										/>

										<RCCGrid
											container
											item
											xs={3}
										>
											<RCCInput 
												register={register}
												name={`adtbb004[${index}].adb004Codigo`} 
												label={translate("Código")}
												defaultValue={itemAudit.adb004Codigo}
												maxLength={20}
												error={error.adtbb004 && error.adtbb004[index]?.adb004Codigo}
												onBlurCapture={() => onBlurItens()}
												required
											/>
										</RCCGrid>
										<RCCGrid
											container
											item
											xs={7}
										>
											<RCCInput 
												register={register}
												name={`adtbb004[${index}].adb004Descricao`} 
												label={translate("Descrição")}
												defaultValue={itemAudit.adb004Descricao}
												maxLength={200}
												error={error.adtbb004 && error.adtbb004[index]?.adb004Descricao}
												onBlurCapture={() => onBlurItens()}
												required
											/>
										</RCCGrid>
										<SortableHandle>
											<RCCTooltip
												color="grey"
												text={translate("Ordenar")}
											>
												<RCCButton
													color="transparent"
													icon={{
														name: "arrows",
														color: "var(--grey)"
													}}
													layout="isRounded"
													disabled={disabled}
													tabIndex={-1}
												/>
											</RCCTooltip>	
										</SortableHandle>
										<RCCTooltip
											color="red"
											text={translate("Remover")}
										>
											<RCCButton 
												color="red"
												icon={{
													name: "trash"
												}}
												onClick={() => handleRemoveItem(index)}
												disabled={disabled}
											/>
										</RCCTooltip>
									</RCCGrid>
								</SortableItem>
							))}
						</SortableContainer>
					</RCCGrid>
				</RCCGrid>
			</RCCGrid>
		</RCCGrid>
	);
};

export default forwardRef(TabAuditItems);
